import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const H2 = ({ id, children }) => (
  <h2 id={id} className="text-[22px] md:text-[26px] font-extrabold text-stone-900 mt-10 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="text-[15px] text-stone-700 leading-relaxed mb-3">{children}</p>
);

const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-[15px] text-stone-700 leading-relaxed mb-4">{children}</ul>
);

const TermsOfService = () => {
  const lastUpdated = 'November 2025';
  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <p className="text-[12px] font-bold uppercase tracking-widest text-[#F97316] mb-2">Legal</p>
        <h1 className="text-[38px] md:text-[46px] font-extrabold text-stone-900 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Terms of Service
        </h1>
        <p className="mt-3 text-[13px] text-stone-500">Last updated: {lastUpdated}</p>

        <div className="mt-10">
          <P>
            These Terms of Service ("Terms") govern your access to and use of the Ambanji website,
            applications, and services (collectively, the "Service") operated by
            <strong> Crystal Blue Marketing LLC</strong>, an Ohio limited liability company ("Ambanji," "we,"
            "us," or "our"). By using the Service you agree to be bound by these Terms and by our Privacy
            Policy, which is incorporated here by reference.
          </P>

          <H2 id="eligibility">1. Eligibility</H2>
          <P>
            You must be at least eighteen (18) years old and legally able to enter into a binding contract to
            use the Service. If you are using the Service on behalf of a business, you represent that you have
            authority to bind that business to these Terms.
          </P>

          <H2 id="account">2. Your Account</H2>
          <P>
            You are responsible for maintaining the confidentiality of your account credentials and for all
            activity that occurs under your account. Notify us immediately at support@ambanji.com if you
            suspect unauthorized use.
          </P>

          <H2 id="description">3. Description of Service</H2>
          <P>Ambanji provides a subscription-based service that may include:</P>
          <UL>
            <li>Automated Google Business Profile monitoring and audits.</li>
            <li>Local ranking heatmaps generated through third-party audit providers.</li>
            <li>AI-generated draft posts, review replies, image captions, and descriptions.</li>
            <li>An approval queue that publishes approved content to third-party platforms on your behalf.</li>
            <li>Citation and reputation tracking.</li>
            <li>SMS review-request delivery through our A2P-10DLC-registered sender.</li>
            <li>White-label reporting and, on eligible plans, a client-facing portal.</li>
          </UL>
          <P>
            Features vary by subscription tier. We may add, modify, or discontinue features at our
            discretion; we will provide reasonable notice of material changes.
          </P>

          <H2 id="subscription">4. Subscriptions, Billing, and Payment</H2>
          <UL>
            <li>Subscriptions renew automatically each month or year, based on the plan you select, until you cancel.</li>
            <li>All payments are processed by Stripe. By subscribing you agree to Stripe's payment terms in addition to these Terms.</li>
            <li>Prices are listed in U.S. dollars and exclude applicable taxes, which will be calculated and added at checkout where required.</li>
            <li>Failed payments may result in suspension of the Service until the balance is resolved.</li>
            <li>We may change prices for future billing cycles. Any price change will be communicated at least thirty (30) days in advance.</li>
          </UL>

          <H2 id="cancellation">5. Cancellation and Refunds</H2>
          <P>
            You may cancel your subscription at any time from your account dashboard or by emailing
            support@ambanji.com. Upon cancellation, you will retain access to the paid features through the
            end of the current billing period; the Service will not renew after that.
          </P>
          <P>
            <strong>Ambanji does not offer refunds for partial billing periods, unused time, or previously
            paid amounts.</strong> This no-refund policy applies to monthly and annual subscriptions and to
            add-on services. Exceptions may be granted at our sole discretion in the event of a documented
            billing error.
          </P>

          <H2 id="acceptable-use">6. Acceptable Use</H2>
          <P>You agree not to:</P>
          <UL>
            <li>Use the Service to publish false, deceptive, defamatory, or unlawful content.</li>
            <li>Solicit or generate fake reviews, ratings, or engagement of any kind.</li>
            <li>Attempt to reverse engineer, scrape, or circumvent any part of the Service.</li>
            <li>Use the Service in a way that violates Google Business Profile guidelines or the terms of any connected third-party platform.</li>
            <li>Send SMS or email messages to recipients who have not provided lawful consent.</li>
            <li>Interfere with the operation of the Service, other users, or our infrastructure.</li>
          </UL>
          <P>
            We may suspend or terminate your account for violations of this section, with or without notice,
            and we reserve the right to report unlawful activity to the appropriate authorities.
          </P>

          <H2 id="connected">7. Connected Third-Party Services</H2>
          <P>
            The Service integrates with third-party platforms including Google Business Profile, our CRM
            provider (through the Ambanji Relay integration), Stripe, and Local Falcon. Your use of those
            platforms is subject to their respective terms and policies. We are not responsible for the
            availability, accuracy, or actions of third-party services, and outages on those platforms may
            temporarily affect Ambanji functionality.
          </P>

          <H2 id="content">8. Your Content and Data</H2>
          <P>
            You retain all rights to the business data, keywords, images, and content you provide to the
            Service ("Your Content"). You grant Ambanji a limited, non-exclusive, worldwide, royalty-free
            license to use, store, transmit, and display Your Content solely to operate the Service and
            provide it to you. You represent that you have all rights necessary to grant this license.
          </P>

          <H2 id="ai">9. AI-Generated Content</H2>
          <P>
            Ambanji uses artificial intelligence to draft posts, review replies, image captions, and other
            content on your behalf. AI outputs are drafts intended for your review. You are solely
            responsible for reviewing and approving any content before it is published, and you understand
            that AI may occasionally generate inaccurate, biased, or otherwise imperfect drafts. We do not
            claim ownership of the outputs generated for you; you own the content you approve and publish.
          </P>

          <H2 id="ip">10. Intellectual Property</H2>
          <P>
            Ambanji, the Ambanji name and logo, and all software, designs, and documentation associated with
            the Service are the property of Crystal Blue Marketing LLC and are protected by U.S. and
            international copyright and trademark law. Nothing in these Terms grants you any right to use
            our marks except as required to use the Service.
          </P>

          <H2 id="disclaimers">11. Disclaimers</H2>
          <P>
            The Service is provided <strong>"AS IS" and "AS AVAILABLE"</strong> without warranties of any
            kind, express or implied, including implied warranties of merchantability, fitness for a
            particular purpose, and non-infringement.
          </P>
          <P>
            <strong>No ranking guarantee.</strong> Search engine and map rankings are controlled by third
            parties (including Google) using algorithms outside our control. We do not guarantee any
            specific ranking, traffic, lead volume, or business outcome. Ambanji only performs white-hat
            optimization techniques consistent with Google's Business Profile guidelines.
          </P>

          <H2 id="liability">12. Limitation of Liability</H2>
          <P>
            To the maximum extent permitted by law, Crystal Blue Marketing LLC and its officers, employees,
            and agents will not be liable for any indirect, incidental, special, consequential, or punitive
            damages, or any loss of profits or revenues, whether incurred directly or indirectly.
          </P>
          <P>
            Our total liability to you for any claim arising out of or relating to the Service or these
            Terms will not exceed the greater of one hundred U.S. dollars ($100) or the amount you paid us
            in the twelve (12) months preceding the claim.
          </P>

          <H2 id="indemnify">13. Indemnification</H2>
          <P>
            You agree to indemnify and hold harmless Crystal Blue Marketing LLC from any claim, damage, or
            expense (including reasonable attorneys' fees) arising out of your use of the Service, your
            violation of these Terms, or your violation of any applicable law or third-party right.
          </P>

          <H2 id="termination">14. Termination</H2>
          <P>
            You may cancel at any time as described in Section 5. We may suspend or terminate your access to
            the Service if you materially breach these Terms, if we are required to do so by law, or if
            continued provision would create unreasonable risk to us or other users. Sections that by their
            nature should survive termination will survive.
          </P>

          <H2 id="governing-law">15. Governing Law and Venue</H2>
          <P>
            These Terms are governed by the laws of the State of Ohio, without regard to conflict-of-law
            principles. Any dispute arising under or relating to these Terms will be resolved exclusively in
            the state or federal courts located in Franklin County, Ohio, and you consent to personal
            jurisdiction in those courts.
          </P>

          <H2 id="changes">16. Changes to These Terms</H2>
          <P>
            We may update these Terms from time to time. When we make material changes we will update the
            "Last updated" date above and, where appropriate, notify you by email or through the Service
            before the changes take effect. Continued use of the Service after changes take effect means you
            accept the revised Terms.
          </P>

          <H2 id="misc">17. Miscellaneous</H2>
          <P>
            If any provision of these Terms is held unenforceable, the remaining provisions will remain in
            full force. Our failure to enforce any right does not waive that right. These Terms, together
            with the Privacy Policy, are the entire agreement between you and Crystal Blue Marketing LLC
            regarding the Service.
          </P>

          <H2 id="contact">18. Contact</H2>
          <div className="rounded-xl border border-stone-200 bg-white p-5 text-[14px] text-stone-700 leading-relaxed">
            <p><strong>Crystal Blue Marketing LLC</strong></p>
            <p>501 Westmoor Pl, Columbus, OH 43204</p>
            <p>Email: support@ambanji.com</p>
            <p>SMS sender: +1 (614) 902-4952</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfService;
